// Tags each row with its source so the operator view can distinguish special
// services from fixed route (specialName is unreliable — it is often blank).
export const mergeOperatorRows = (fixedData, specialData) => {
  const fixed = (fixedData || []).map((row) => ({ ...row, isSpecial: false }));
  const special = (specialData || []).map((row) => ({ ...row, isSpecial: true }));
  return [...fixed, ...special];
};

// Fixed rows order by signup time; special rows have none, so they interlace by
// their Time Out (scheduledPulloutTimeFmt). Treats blank signup as missing.
export const operatorRowSortTime = (row) => {
  const signup = row.signUpTimeFmt;
  if (signup && signup.trim() !== "") return signup;
  return row.scheduledPulloutTimeFmt ?? "";
};

// Operator board column widths, in percent, summing to 100. Fixed-route rows fill
// every column, so these are the widths the board falls back to.
export const OPERATOR_BASE_WIDTHS = {
  box: 5,
  run: 10,
  route: 10,
  block: 8,
  signupTime: 12,
  bus: 10,
  loc: 8,
  exc: 8,
  note: 29,
};

// Columns that give up width when no row has a value for them. `floor` is what
// each keeps for its header — the header row is already two lines tall ("Signup /
// Time"), so letting "Operator Note" wrap costs no vertical space.
const DONOR_COLUMNS = [
  { key: "bus", read: (row) => row.lastVehicle, floor: 6 },
  { key: "loc", read: (row) => row.pulloutLocation, floor: 5 },
  { key: "exc", read: (row) => row.pulloutVarianceReason, floor: 5 },
  { key: "note", read: (row) => row.noteToOperator, floor: 9 },
];

// Columns that absorb freed width, in priority order. Route carries the long
// special-service names, so it is filled first.
const RECIPIENT_COLUMNS = [
  { key: "route", max: 30 },
  { key: "run", max: 14 },
];

const isBlank = (value) => `${value ?? ""}`.trim() === "";

// Widens Route (then Run) using width surrendered by columns that are empty
// across the whole board, so long special-service values fit before wrapping has
// to rescue them. Always totals 100%.
export const computeOperatorColumnWidths = (rows) => {
  const widths = { ...OPERATOR_BASE_WIDTHS };
  if (!rows || rows.length === 0) {
    return widths;
  }

  let freed = 0;
  DONOR_COLUMNS.forEach(({ key, read, floor }) => {
    if (rows.every((row) => isBlank(read(row)))) {
      freed += widths[key] - floor;
      widths[key] = floor;
    }
  });

  RECIPIENT_COLUMNS.forEach(({ key, max }) => {
    const give = Math.min(max - widths[key], freed);
    if (give > 0) {
      widths[key] += give;
      freed -= give;
    }
  });

  // Any surplus stays with Operator Note so the columns still total 100%.
  widths.note += freed;
  return widths;
};
